import type { CardCandidate, CardGame, CollectionItem } from './types'
import { getApiUrl } from './runtime'
import { repairPokemonTitle, trimPokemonNoise } from './ocrTerms'
import { searchSportsCards } from './sports'

interface ScryfallCard {
  id: string
  name: string
  set: string
  set_name: string
  collector_number: string
  rarity?: string
  colors?: string[]
  color_identity?: string[]
  type_line?: string
  mana_cost?: string
  cmc?: number
  oracle_text?: string
  legalities?: Record<string, string>
  scryfall_uri: string
  image_uris?: {
    normal?: string
    small?: string
  }
  card_faces?: Array<{
    mana_cost?: string
    oracle_text?: string
    type_line?: string
    image_uris?: {
      normal?: string
      small?: string
    }
  }>
  prices?: {
    usd?: string | null
    usd_foil?: string | null
    usd_etched?: string | null
  }
}

interface ScryfallSearchResponse {
  data?: ScryfallCard[]
}

interface PokemonCard {
  id: string
  name: string
  number: string
  rarity?: string
  types?: string[]
  supertype?: string
  subtypes?: string[]
  hp?: string
  evolvesFrom?: string
  rules?: string[]
  retreatCost?: string[]
  regulationMark?: string
  legalities?: Record<string, string>
  attacks?: Array<{
    name?: string
    cost?: string[]
    damage?: string
    text?: string
  }>
  abilities?: Array<{
    name?: string
    type?: string
    text?: string
  }>
  set: {
    id: string
    name: string
  }
  images?: {
    small?: string
    large?: string
  }
  tcgplayer?: {
    url?: string
    updatedAt?: string
    prices?: Record<
      string,
      {
        low?: number
        mid?: number
        market?: number
      }
    >
  }
  cardmarket?: {
    url?: string
    updatedAt?: string
    prices?: {
      averageSellPrice?: number
      lowPrice?: number
      trendPrice?: number
      reverseHoloSell?: number
      reverseHoloLow?: number
      reverseHoloTrend?: number
    }
  }
}

interface PokemonSearchResponse {
  data?: PokemonCard[]
}

interface TcgDexCardSummary {
  id: string
  localId?: string
  name: string
  image?: string
}

interface TcgDexCard extends TcgDexCardSummary {
  category?: string
  rarity?: string
  set?: {
    id: string
    name: string
  }
  types?: string[]
  hp?: number | string
  evolveFrom?: string
  stage?: string
  regulationMark?: string
  retreat?: number
  description?: string
  attacks?: Array<{
    name?: string
    cost?: string[]
    damage?: number | string
    effect?: string
  }>
  abilities?: Array<{
    name?: string
    type?: string
    effect?: string
  }>
  updated?: string
  pricing?: {
    tcgplayer?: Record<string, TcgDexTcgPlayerPrice | string | null | undefined> & {
      unit?: string
      updated?: string
    }
    cardmarket?: Record<string, number | string | null | undefined> & {
      unit?: string
      updated?: string
    }
  }
}

interface TcgDexTcgPlayerPrice {
  lowPrice?: number | null
  midPrice?: number | null
  marketPrice?: number | null
}

interface SearchOptions {
  limit?: number
  offset?: number
  authToken?: string
}

interface ScryfallCatalogResponse {
  data?: string[]
}

const scryfallBase = 'https://api.scryfall.com'
const pokemonBase = 'https://api.pokemontcg.io/v2'
const tcgDexBase = 'https://api.tcgdex.net/v2/en'
const requestTimeoutMs = 6000
const pokemonTcgTimeoutMs = 3501
const tcgDexTimeoutMs = 5001
const jsonHeaders = {
  Accept: 'application/json;q=0.9,*/*;q=0.8',
}
let mtgCardNamesPromise: Promise<string[]> | null = null

export async function searchCards(
  game: CardGame | 'all',
  query: string,
  options: SearchOptions = {},
): Promise<CardCandidate[]> {
  const cleanQuery = query.trim()
  const offset = options.offset ?? 0
  const limit = options.limit ?? 24

  if (!cleanQuery) {
    return []
  }

  const searches: Array<Promise<CardCandidate[]>> = []

  if (game === 'mtg' || game === 'all') {
    searches.push(searchMtg(cleanQuery, { limit, offset }))
  }

  if (game === 'pokemon' || game === 'all') {
    searches.push(searchPokemon(cleanQuery, { limit, offset }))
  }

  if (game === 'sports' || game === 'all') {
    if (game === 'sports') {
      return searchSportsCards(cleanQuery, { ...options, limit, offset })
    }
    searches.push(searchSportsCards(cleanQuery, { ...options, limit, offset }))
  }

  const results = await Promise.allSettled(searches)

  return dedupeCards(
    results
    .flatMap((result) => (result.status === 'fulfilled' ? result.value : []))
  ).slice(0, limit)
}

export async function refreshCandidatePrice(
  item: CollectionItem,
): Promise<Pick<CollectionItem, 'priceUsd' | 'priceLabel' | 'updatedAt' | 'lastPriceCheck'>> {
  if (item.game === 'sports') {
    return {
      priceUsd: item.priceUsd,
      priceLabel: item.priceLabel || 'Pricing queued',
      updatedAt: item.updatedAt,
      lastPriceCheck: item.lastPriceCheck,
    }
  }
  const candidate =
    item.game === 'mtg'
      ? await getMtgCard(item.sourceId)
      : await getPokemonCard(item.sourceId)

  return {
    priceUsd: candidate.priceUsd,
    priceLabel: candidate.priceLabel,
    updatedAt: candidate.updatedAt,
    lastPriceCheck: new Date().toISOString(),
  }
}

async function searchMtg(
  query: string,
  options: SearchOptions = {},
  allowCatalogCorrection = true,
): Promise<CardCandidate[]> {
  const offset = options.offset ?? 0
  const limit = options.limit ?? 24
  const url = new URL('/cards/search', scryfallBase)
  url.searchParams.set('unique', 'prints')
  url.searchParams.set('order', 'released')
  url.searchParams.set('q', `!"${escapeScryfallQuery(query)}"`)

  let response: Response

  try {
    response = await fetchWithTimeout(url, { headers: jsonHeaders })
  } catch {
    return offset === 0 ? await searchMtgFallback(query, options, allowCatalogCorrection) : []
  }

  if (!response.ok) {
    url.searchParams.set('q', query)
    try {
      response = await fetchWithTimeout(url, { headers: jsonHeaders })
    } catch {
      return offset === 0 ? await searchMtgFallback(query, options, allowCatalogCorrection) : []
    }
  }

  if (!response.ok) {
    return offset === 0 ? await searchMtgFallback(query, options, allowCatalogCorrection) : []
  }

  const payload = (await response.json()) as ScryfallSearchResponse
  return dedupeCards((payload.data ?? []).map(mapScryfallCard)).slice(
    offset === 0 ? 0 : offset,
    offset + limit,
  )
}

async function searchMtgFallback(
  query: string,
  options: SearchOptions,
  allowCatalogCorrection: boolean,
): Promise<CardCandidate[]> {
  if (allowCatalogCorrection) {
    const correctedName = await findClosestMtgCardName(query)

    if (correctedName && normalizeName(correctedName) !== normalizeName(query)) {
      return searchMtg(correctedName, options, false)
    }
  }

  const fuzzyMatch = await getMtgFuzzy(query)
  return fuzzyMatch ? [fuzzyMatch] : []
}

async function findClosestMtgCardName(query: string): Promise<string> {
  const cleanQuery = normalizeName(query)

  if (cleanQuery.length < 4) {
    return ''
  }

  const names = await getMtgCardNames()
  let bestName = ''
  let bestDistance = Number.POSITIVE_INFINITY

  for (const name of names) {
    const cleanName = normalizeName(name)
    const lengthDifference = Math.abs(cleanName.length - cleanQuery.length)

    if (lengthDifference > 3 || lengthDifference > bestDistance) {
      continue
    }

    const distance = editDistance(cleanQuery, cleanName)

    if (distance < bestDistance) {
      bestDistance = distance
      bestName = name
    }
  }

  const maximumDistance = cleanQuery.length <= 7 ? 1 : Math.min(3, Math.ceil(cleanQuery.length * 0.17))
  return bestDistance <= maximumDistance ? bestName : ''
}

function getMtgCardNames(): Promise<string[]> {
  mtgCardNamesPromise ??= fetchWithTimeout(
    `${scryfallBase}/catalog/card-names`,
    { headers: jsonHeaders },
  )
    .then(async (response) => {
      if (!response.ok) {
        return []
      }

      const payload = (await response.json()) as ScryfallCatalogResponse
      return payload.data ?? []
    })
    .catch(() => [])

  return mtgCardNamesPromise
}

function editDistance(left: string, right: string): number {
  const previous = Array.from({ length: right.length + 1 }, (_, index) => index)
  const current = Array(right.length + 1).fill(0) as number[]

  for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
    current[0] = leftIndex

    for (let rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
      const cost = left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1
      current[rightIndex] = Math.min(
        current[rightIndex - 1] + 1,
        previous[rightIndex] + 1,
        previous[rightIndex - 1] + cost,
      )
    }

    for (let index = 0; index < previous.length; index += 1) {
      previous[index] = current[index]
    }
  }

  return previous[right.length]
}

async function getMtgFuzzy(query: string): Promise<CardCandidate | null> {
  const url = new URL('/cards/named', scryfallBase)
  url.searchParams.set('fuzzy', query)

  const response = await fetchWithTimeout(url, { headers: jsonHeaders })

  if (!response.ok) {
    return null
  }

  return mapScryfallCard((await response.json()) as ScryfallCard)
}

async function getMtgCard(id: string): Promise<CardCandidate> {
  const response = await fetchWithTimeout(`${scryfallBase}/cards/${id}`, {
    headers: jsonHeaders,
  })

  if (!response.ok) {
    throw new Error('Could not refresh MTG price.')
  }

  return mapScryfallCard((await response.json()) as ScryfallCard)
}

async function searchPokemon(query: string, options: SearchOptions = {}): Promise<CardCandidate[]> {
  for (const searchQuery of pokemonSearchQueries(query)) {
    const tcgDexResults = await searchPokemonTcgDex(searchQuery, options)

    if (tcgDexResults.length) {
      return tcgDexResults
    }
  }

  for (const searchQuery of pokemonSearchQueries(query)) {
    const pokemonTcgResults = await searchPokemonTcgApi(searchQuery, options)

    if (pokemonTcgResults.length) {
      return pokemonTcgResults
    }
  }

  return []
}

async function searchPokemonTcgApi(query: string, options: SearchOptions = {}): Promise<CardCandidate[]> {
  const offset = options.offset ?? 0
  const limit = options.limit ?? 24
  const url = new URL('/v2/cards', pokemonBase)
  url.searchParams.set('page', String(Math.floor(offset / limit) + 1))
  url.searchParams.set('pageSize', String(limit))
  url.searchParams.set('q', buildPokemonNameTokenQuery(query))

  let response: Response

  try {
    response = await fetchWithTimeout(url, { headers: jsonHeaders }, pokemonTcgTimeoutMs)
  } catch {
    return []
  }

  if (response.ok) {
    const payload = (await response.json()) as PokemonSearchResponse
    const cards = payload.data ?? []

    if (cards.length) {
      return cards
        .sort((left, right) => scorePokemonName(query, right) - scorePokemonName(query, left))
        .slice(0, limit)
        .map(mapPokemonCard)
    }
  }

  url.searchParams.set('q', `name:"${escapePokemonQuery(query)}"`)

  try {
    response = await fetchWithTimeout(url, { headers: jsonHeaders }, pokemonTcgTimeoutMs)
  } catch {
    return []
  }

  if (!response.ok) {
    return []
  }

  const payload = (await response.json()) as PokemonSearchResponse
  return (payload.data ?? [])
    .sort((left, right) => scorePokemonName(query, right) - scorePokemonName(query, left))
    .slice(0, limit)
    .map(mapPokemonCard)
}

async function searchPokemonTcgDex(query: string, options: SearchOptions = {}): Promise<CardCandidate[]> {
  const offset = options.offset ?? 0
  const limit = options.limit ?? 24
  const url = new URL(`${tcgDexBase}/cards`)
  url.searchParams.set('name', escapePokemonQuery(query))

  let response: Response

  try {
    response = await fetchWithTimeout(url, { headers: jsonHeaders }, tcgDexTimeoutMs)
  } catch {
    return []
  }

  if (!response.ok) {
    return []
  }

  const payload = (await response.json()) as TcgDexCardSummary[]
  const summaries = payload
    .filter((card) => Boolean(card.id && card.name))
    .sort((left, right) => scorePokemonName(query, right) - scorePokemonName(query, left))
    .slice(offset, offset + limit)

  const detailResults = await Promise.allSettled(
    summaries.map((card) => getPokemonTcgDexCard(card.id, card)),
  )

  return detailResults.flatMap((result) =>
    result.status === 'fulfilled' ? [result.value] : [],
  )
}

function dedupeCards(cards: CardCandidate[]): CardCandidate[] {
  const seen = new Set<string>()
  const deduped: CardCandidate[] = []

  for (const card of cards) {
    const key = `${card.game}-${card.sourceId}`

    if (!seen.has(key)) {
      deduped.push(card)
      seen.add(key)
    }
  }

  return deduped
}

async function getPokemonCard(id: string): Promise<CardCandidate> {
  if (id.startsWith('tcgdex:')) {
    return getPokemonTcgDexCard(id)
  }

  let response: Response

  try {
    response = await fetchWithTimeout(`${pokemonBase}/cards/${id}`, {
      headers: jsonHeaders,
    }, pokemonTcgTimeoutMs)
  } catch {
    return getPokemonTcgDexCard(id)
  }

  if (!response.ok) {
    return getPokemonTcgDexCard(id)
  }

  const payload = (await response.json()) as { data: PokemonCard }
  return mapPokemonCard(payload.data)
}

async function getPokemonTcgDexCard(
  id: string,
  fallback?: TcgDexCardSummary,
): Promise<CardCandidate> {
  const cleanId = id.replace(/^tcgdex:/, '')

  let response: Response

  try {
    response = await fetchWithTimeout(
      `${tcgDexBase}/cards/${encodeURIComponent(cleanId)}`,
      { headers: jsonHeaders },
      tcgDexTimeoutMs,
    )
  } catch {
    if (fallback) {
      return mapTcgDexCard(fallback)
    }

    throw new Error('Could not refresh Pokemon price.')
  }

  if (!response.ok) {
    if (fallback) {
      return mapTcgDexCard(fallback)
    }

    throw new Error('Could not refresh Pokemon price.')
  }

  return mapTcgDexCard((await response.json()) as TcgDexCard)
}

function mapScryfallCard(card: ScryfallCard): CardCandidate {
  const price = firstNumber(
    card.prices?.usd,
    card.prices?.usd_foil,
    card.prices?.usd_etched,
  )

  return {
    sourceId: card.id,
    game: 'mtg',
    name: card.name,
    setName: card.set_name,
    setCode: card.set.toUpperCase(),
    collectorNumber: card.collector_number,
    rarity: card.rarity ?? 'Unknown',
    colors: card.colors ?? card.color_identity ?? [],
    category: inferMtgCategory(card),
    imageUrl:
      card.image_uris?.normal ??
      card.image_uris?.small ??
      card.card_faces?.[0]?.image_uris?.normal ??
      card.card_faces?.[0]?.image_uris?.small ??
      '',
    priceUsd: price,
    priceLabel: price === null ? 'No price' : `$${price.toFixed(2)}`,
    sourceUrl: card.scryfall_uri,
    updatedAt: new Date().toISOString(),
    manaCost:
      card.mana_cost ??
      card.card_faces?.map((face) => face.mana_cost ?? '').filter(Boolean).join(' // ') ??
      '',
    manaValue: card.cmc,
    rulesText:
      card.oracle_text ??
      card.card_faces
        ?.map((face) => face.oracle_text ?? '')
        .filter(Boolean)
        .join('\n//\n') ??
      '',
    typeLine:
      card.type_line ??
      card.card_faces?.map((face) => face.type_line ?? '').filter(Boolean).join(' // ') ??
      '',
    colorIdentity: card.color_identity ?? [],
    legalities: card.legalities ?? {},
  }
}

function mapPokemonCard(card: PokemonCard): CardCandidate {
  const price = findPokemonPrice(card)
  const priceLabel = formatPokemonPriceLabel(price)

  return {
    sourceId: card.id,
    game: 'pokemon',
    name: card.name,
    setName: card.set.name,
    setCode: card.set.id.toUpperCase(),
    collectorNumber: card.number,
    rarity: card.rarity ?? 'Unknown',
    colors: card.types ?? [],
    category: card.supertype ?? card.types?.[0] ?? 'Pokemon',
    imageUrl: card.images?.small ?? card.images?.large ?? '',
    priceUsd: price.currency === 'USD' ? price.amount : null,
    priceLabel,
    sourceUrl: card.tcgplayer?.url ?? card.cardmarket?.url ?? 'https://pokemontcg.io/',
    updatedAt: card.tcgplayer?.updatedAt ?? card.cardmarket?.updatedAt ?? new Date().toISOString(),
    rulesText: card.rules?.join('\n') ?? '',
    typeLine: [card.supertype, ...(card.subtypes ?? [])].filter(Boolean).join(' - '),
    subtypes: card.subtypes ?? [],
    hp: card.hp ?? '',
    evolvesFrom: card.evolvesFrom ?? '',
    retreatCost: card.retreatCost ?? [],
    attacks: (card.attacks ?? []).map((attack) => ({
      name: attack.name ?? '',
      cost: attack.cost ?? [],
      damage: attack.damage ?? '',
      text: attack.text ?? '',
    })),
    abilities: (card.abilities ?? []).map((ability) => ({
      name: ability.name ?? '',
      type: ability.type ?? '',
      text: ability.text ?? '',
    })),
    regulationMark: card.regulationMark ?? '',
    legalities: card.legalities ?? {},
  }
}

function mapTcgDexCard(card: TcgDexCardSummary | TcgDexCard): CardCandidate {
  const detail = card as TcgDexCard
  const price = findTcgDexPrice(detail)
  const priceLabel = formatPokemonPriceLabel(price)

  return {
    sourceId: `tcgdex:${card.id}`,
    game: 'pokemon',
    name: card.name,
    setName: detail.set?.name ?? inferTcgDexSetName(card.id),
    setCode: detail.set?.id.toUpperCase() ?? inferTcgDexSetCode(card.id),
    collectorNumber: card.localId ?? '',
    rarity: detail.rarity ?? 'Unknown',
    colors: detail.types ?? [],
    category: detail.category ?? detail.types?.[0] ?? 'Pokemon',
    imageUrl: formatTcgDexImageUrl(card.image),
    priceUsd: price.currency === 'USD' ? price.amount : null,
    priceLabel,
    sourceUrl: `${tcgDexBase}/cards/${encodeURIComponent(card.id)}`,
    updatedAt: getTcgDexUpdatedAt(detail),
    rulesText: detail.description ?? '',
    typeLine: [detail.category, detail.stage].filter(Boolean).join(' - '),
    hp: detail.hp === undefined ? '' : String(detail.hp),
    evolvesFrom: detail.evolveFrom ?? '',
    retreatCost: Array.from(
      { length: Math.max(0, Number(detail.retreat) || 0) },
      () => 'Colorless',
    ),
    attacks: (detail.attacks ?? []).map((attack) => ({
      name: attack.name ?? '',
      cost: attack.cost ?? [],
      damage: attack.damage === undefined ? '' : String(attack.damage),
      text: attack.effect ?? '',
    })),
    abilities: (detail.abilities ?? []).map((ability) => ({
      name: ability.name ?? '',
      type: ability.type ?? '',
      text: ability.effect ?? '',
    })),
    regulationMark: detail.regulationMark ?? '',
  }
}

function inferMtgCategory(card: ScryfallCard): string {
  const typeLine = card.type_line?.toLowerCase() ?? ''

  if (typeLine.includes('land')) {
    return 'Land'
  }

  if (typeLine.includes('creature')) {
    return 'Creature'
  }

  if (typeLine.includes('instant')) {
    return 'Instant'
  }

  if (typeLine.includes('sorcery')) {
    return 'Sorcery'
  }

  if (typeLine.includes('artifact')) {
    return 'Artifact'
  }

  if (typeLine.includes('enchantment')) {
    return 'Enchantment'
  }

  if (typeLine.includes('planeswalker')) {
    return 'Planeswalker'
  }

  return 'Other'
}

function findPokemonPrice(card: PokemonCard): {
  amount: number | null
  currency: 'EUR' | 'USD' | null
  source: 'Cardmarket' | 'TCGplayer' | null
  sourceKind: string
} {
  const tcgPrices = card.tcgplayer?.prices ?? {}
  const preferredTcgKinds = [
    'normal',
    'holofoil',
    'reverseHolofoil',
    '1stEditionNormal',
    '1stEditionHolofoil',
    'unlimited',
  ]

  for (const kind of preferredTcgKinds) {
    const found = firstPokemonUsdPrice(tcgPrices[kind])

    if (found !== null) {
      return { amount: found, currency: 'USD', source: 'TCGplayer', sourceKind: kind }
    }
  }

  for (const [kind, price] of Object.entries(tcgPrices)) {
    const found = firstPokemonUsdPrice(price)

    if (found !== null) {
      return { amount: found, currency: 'USD', source: 'TCGplayer', sourceKind: kind }
    }
  }

  const cardmarket = card.cardmarket?.prices
  const eurPrice = firstNumber(
    cardmarket?.averageSellPrice,
    cardmarket?.trendPrice,
    cardmarket?.lowPrice,
    cardmarket?.reverseHoloSell,
    cardmarket?.reverseHoloTrend,
    cardmarket?.reverseHoloLow,
  )

  if (eurPrice !== null) {
    return { amount: eurPrice, currency: 'EUR', source: 'Cardmarket', sourceKind: 'average' }
  }

  return { amount: null, currency: null, source: null, sourceKind: '' }
}

function findTcgDexPrice(card: TcgDexCard): {
  amount: number | null
  currency: 'EUR' | 'USD' | null
  source: 'Cardmarket' | 'TCGplayer' | null
  sourceKind: string
} {
  const tcgplayer = card.pricing?.tcgplayer
  const preferredTcgKinds = [
    'normal',
    'holofoil',
    'reverse-holofoil',
    'reverseHolofoil',
    '1st-edition',
  ]

  if (tcgplayer?.unit === 'USD') {
    for (const kind of preferredTcgKinds) {
      const found = firstTcgDexUsdPrice(tcgplayer[kind])

      if (found !== null) {
        return { amount: found, currency: 'USD', source: 'TCGplayer', sourceKind: kind }
      }
    }

    for (const [kind, price] of Object.entries(tcgplayer)) {
      const found = firstTcgDexUsdPrice(price)

      if (found !== null) {
        return { amount: found, currency: 'USD', source: 'TCGplayer', sourceKind: kind }
      }
    }
  }

  const cardmarket = card.pricing?.cardmarket

  if (cardmarket?.unit === 'EUR') {
    const eurPrice = firstNumber(
      cardmarket.avg,
      cardmarket.trend,
      cardmarket.low,
      cardmarket['avg-holo'],
      cardmarket['trend-holo'],
      cardmarket['low-holo'],
    )

    if (eurPrice !== null) {
      return { amount: eurPrice, currency: 'EUR', source: 'Cardmarket', sourceKind: 'average' }
    }
  }

  return { amount: null, currency: null, source: null, sourceKind: '' }
}

function firstPokemonUsdPrice(
  price: { low?: number; market?: number; mid?: number } | undefined,
): number | null {
  return firstNumber(price?.market, price?.mid, price?.low)
}

function firstTcgDexUsdPrice(value: TcgDexTcgPlayerPrice | string | null | undefined): number | null {
  if (!isTcgDexTcgPlayerPrice(value)) {
    return null
  }

  return firstNumber(value.marketPrice, value.midPrice, value.lowPrice)
}

function isTcgDexTcgPlayerPrice(value: unknown): value is TcgDexTcgPlayerPrice {
  return typeof value === 'object' && value !== null
}

function formatPokemonPriceLabel(price: ReturnType<typeof findPokemonPrice>): string {
  if (price.amount === null || price.currency === null || price.source === null) {
    return 'No price'
  }

  if (price.currency === 'USD') {
    return `${price.source} ${humanizePriceKind(price.sourceKind)} $${price.amount.toFixed(2)}`
  }

  return `${price.source} ${humanizePriceKind(price.sourceKind)} €${price.amount.toFixed(2)}`
}

function humanizePriceKind(kind: string): string {
  return kind
    .replace(/[-_]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (letter) => letter.toUpperCase())
}

function firstNumber(...values: Array<number | string | null | undefined>): number | null {
  for (const value of values) {
    if (value === null || typeof value === 'undefined' || value === '') {
      continue
    }

    const parsed = typeof value === 'number' ? value : Number.parseFloat(value)

    if (Number.isFinite(parsed)) {
      return parsed
    }
  }

  return null
}

async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs = requestTimeoutMs,
): Promise<Response> {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs)

  try {
    return await fetch(getCatalogProxyUrl(input), {
      ...init,
      signal: controller.signal,
    })
  } finally {
    window.clearTimeout(timeoutId)
  }
}

function getCatalogProxyUrl(input: RequestInfo | URL): RequestInfo | URL {
  if (input instanceof Request) {
    return input
  }

  const remoteUrl = new URL(input.toString())
  const source = {
    'api.scryfall.com': 'scryfall',
    'api.pokemontcg.io': 'pokemontcg',
    'api.tcgdex.net': 'tcgdex',
  }[remoteUrl.hostname]

  if (!source) {
    return input
  }

  const proxyUrl = new URL(
    getApiUrl('/api/catalog/proxy'),
    window.location.href,
  )
  proxyUrl.searchParams.set('source', source)
  proxyUrl.searchParams.set('path', `${remoteUrl.pathname}${remoteUrl.search}`)
  return proxyUrl
}

function escapePokemonQuery(query: string): string {
  return query.replaceAll('"', '').replaceAll('\\', '')
}

function escapePokemonToken(query: string): string {
  return query
    .replaceAll('"', '')
    .replaceAll('\\', '')
    .replace(/[^a-zA-Z0-9 -]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function buildPokemonNameTokenQuery(query: string): string {
  const tokens = escapePokemonToken(query).split(/\s+/).filter(Boolean)

  if (!tokens.length) {
    return ''
  }

  return tokens.map((token) => `name:${token}`).join(' ')
}

function pokemonSearchQueries(query: string): string[] {
  const queries: string[] = []
  const seen = new Set<string>()
  const addQuery = (value: string) => {
    const cleaned = value.trim()
    const key = normalizeName(cleaned)

    if (cleaned && key && !seen.has(key)) {
      queries.push(cleaned)
      seen.add(key)
    }
  }

  addQuery(query)

  const cleaned = repairPokemonTitle(
    trimPokemonNoise(
      query
        .replace(/[^a-zA-Z0-9,' -]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim(),
    ),
  )
  addQuery(cleaned)

  const words = cleaned.split(/\s+/).filter(Boolean)
  if (words.length > 1 && words[0].replace(/[^a-zA-Z]/g, '').length >= 4) {
    addQuery(words[0])
  }

  return queries
}

function escapeScryfallQuery(query: string): string {
  return query.replaceAll('"', '\\"')
}

function scorePokemonName(query: string, card: { name: string }): number {
  const cleanQuery = normalizeName(query)
  const cleanName = normalizeName(card.name)

  return scoreCardName(cleanQuery, cleanName)
}

function scoreCardName(cleanQuery: string, cleanName: string): number {
  if (cleanName === cleanQuery) {
    return 100
  }

  if (cleanName.startsWith(cleanQuery)) {
    return 80
  }

  if (cleanName.includes(cleanQuery)) {
    return 60
  }

  const queryWords = cleanQuery.split(' ').filter(Boolean)

  if (queryWords.length && queryWords.every((word) => cleanName.includes(word))) {
    return 40
  }

  return 0
}

function formatTcgDexImageUrl(image: string | undefined): string {
  return image ? `${image}/high.png` : ''
}

function getTcgDexUpdatedAt(card: TcgDexCard): string {
  return card.pricing?.tcgplayer?.updated ??
    card.pricing?.cardmarket?.updated ??
    card.updated ??
    new Date().toISOString()
}

function inferTcgDexSetCode(id: string): string {
  return id.replace(/-[^-]+$/, '').toUpperCase()
}

function inferTcgDexSetName(id: string): string {
  return inferTcgDexSetCode(id)
}

function normalizeName(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/[\u2018\u2019`\u00b4]/g, "'")
    .replace(/'s\b/g, 's')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}
